import { Bell, PauseCircle, PhoneCall } from 'lucide-react';

import { androidUrl, hasAnyStoreLink, iosUrl } from '@/lib/app-links';
import { motion } from '@/lib/motion';

const BENEFITS = [
  {
    icon: <PhoneCall className="h-5 w-5" />,
    title: 'Every call, logged',
    desc: 'Calls, transcripts, AI summaries and leads — all on your phone.',
  },
  {
    icon: <Bell className="h-5 w-5" />,
    title: 'Know the moment it happens',
    desc: 'A push notification after every call with the outcome and summary.',
  },
  {
    icon: <PauseCircle className="h-5 w-5" />,
    title: 'Stay in control',
    desc: 'Pause or resume your agent, tweak the greeting, watch your minutes.',
  },
];

/**
 * Landing-page mobile app section. Store CTAs are config-driven
 * (VITE_IOS_APP_URL / VITE_ANDROID_APP_URL) and hidden until both are set —
 * see lib/app-links.ts. Never render a store badge without a real URL.
 */
export function MobileAppSection(): JSX.Element {
  return (
    <section id="mobile-app" className="scroll-mt-24 container py-14 sm:py-20">
      <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
        <div>
          <span className="text-xs font-semibold uppercase tracking-widest text-[var(--brand-accent)]">
            Mobile app
          </span>
          <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
            Your receptionist in your pocket.{' '}
            <span className="bg-gradient-to-r from-[var(--brand-accent)] to-fuchsia-500 bg-clip-text text-transparent">
              Free
            </span>{' '}
            with every plan.
          </h2>

          <ul className="mt-8 space-y-5">
            {BENEFITS.map((b) => (
              <li key={b.title} className="flex items-start gap-4">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[var(--brand-accent)]/10 text-[var(--brand-accent)]">
                  {b.icon}
                </div>
                <div>
                  <p className="font-semibold">{b.title}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{b.desc}</p>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-8">
            <StoreCtas />
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-4 -z-10 rounded-3xl bg-gradient-to-tr from-[var(--brand-accent)]/20 to-emerald-500/10 blur-2xl" />
          <PhoneMockup />
        </div>
      </div>
    </section>
  );
}

/** Official store badge once its URL is configured (apps are live — badge
 *  artwork is allowed from release), "Coming soon" pill until then — never a
 *  badge pointing at a dead URL. Badge files: public/badges/ (unmodified
 *  Apple SVG; Google PNG trimmed of its transparent padding only). */
function StoreCtas(): JSX.Element {
  const stores = [
    { name: 'App Store', url: iosUrl, live: 'Download on the App Store', badge: '/badges/app-store.svg' },
    { name: 'Google Play', url: androidUrl, live: 'Get it on Google Play', badge: '/badges/google-play.png' },
  ];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        {stores.map((s) =>
          s.url ? (
            <a
              key={s.name}
              href={s.url}
              rel="noopener"
              aria-label={s.live}
              className="inline-flex rounded-lg transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              <img src={s.badge} alt="" className="h-12 w-auto" height={48} />
            </a>
          ) : (
            <span
              key={s.name}
              className="inline-flex h-11 items-center gap-2 rounded-xl border border-dashed border-border px-4 text-sm"
            >
              <span className="font-semibold">{s.name}</span>
              <span className="rounded-full bg-[var(--brand-accent)]/15 px-2 py-0.5 text-xs font-medium text-[var(--brand-accent)]">
                Coming soon
              </span>
            </span>
          ),
        )}
      </div>
      {iosUrl && androidUrl ? null : (
        <p className="text-sm text-muted-foreground">
          {hasAnyStoreLink ? 'More platforms' : 'iPhone & Android apps'} coming soon — included free with every plan.{' '}
          <a href="/contact" className="font-medium text-[var(--brand-accent)] hover:underline">
            Get notified
          </a>
        </p>
      )}
    </div>
  );
}

/** CSS-mocked phone frame — swap for real screenshots once supplied
 *  (public/app/*.png). See "User actions" in the sender-lock/marketing plan. */
function PhoneMockup(): JSX.Element {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="mx-auto w-[260px] overflow-hidden rounded-[2rem] border-8 border-neutral-900 bg-neutral-950 shadow-2xl"
    >
      <div className="flex items-center justify-between px-4 pt-3 text-[10px] text-white/60">
        <span>9:41</span>
        <span className="h-2 w-16 rounded-full bg-white/20" />
      </div>
      <div className="p-4">
        <p className="text-xs font-medium text-white/60">Today</p>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <MockStat label="Calls" value="12" />
          <MockStat label="Minutes used" value="38%" accent />
        </div>
        <div className="mt-3 space-y-2">
          {['New call — booked', 'New call — transferred', 'New call — voicemail'].map((label) => (
            <div key={label} className="rounded-lg bg-white/5 px-3 py-2 text-[11px] text-white/80">
              {label}
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

function MockStat({ label, value, accent }: { label: string; value: string; accent?: boolean }): JSX.Element {
  return (
    <div className="rounded-lg bg-white/5 p-2.5">
      <p className="text-[10px] text-white/50">{label}</p>
      <p className={`mt-0.5 text-sm font-semibold ${accent ? 'text-[var(--brand-accent)]' : 'text-white'}`}>
        {value}
      </p>
    </div>
  );
}
